import {
  Button,
  Checkbox,
  Dialog,
  FormControlLabel,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getCurrentUser } from '../../../store/redux/reducers/currentUserSlice'
import { StyledContainerColumnLeft, StyledContainerRowStart } from '../../../styles/ContainerStyles'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import { getGap } from '../../../styles/stylePropsFunctions'
import AutocompleteEmployees from '../../Notification/AutocompleteEmployees'
import { useSelector } from 'react-redux'
import { Project, User } from '../../../store/types'
import ConfirmationDialog from '../../Dialog/ConfirmationDialog'
import DiscardChangesDialog from '../../Dialog/DiscardChangesDialog'
import SettingTile from '../../../views/GeneralSettingsView/SettingTile'
import CheckIcon from '@mui/icons-material/Check'
import { useSendMissingHoursNotificationMutation } from '../../../store/redux/reducers/pushNotificationSlice'
import { useNotification } from '../../../store/redux/hooks'
import AutocompleteLeaders from '../../Notification/AutocompleteLeaders'
import AutocompleteProjects from '../../Notification/AutocompleteProjects'

type MissingHoursNotificationDialogProps = {
  button?: boolean
}

const MissingHoursNotificationDialog = ({ button = false }: MissingHoursNotificationDialogProps) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const { displayNotification } = useNotification()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [open, setOpen] = useState(false)
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false)
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState<boolean>(false)
  const [isSendButtonDisabled, setIsSendButtonDisabled] = useState(true)
  const [selectedEmployees, setSelectedEmployees] = useState<User[]>([])
  const [selectedLeaders, setSelectedLeaders] = useState<User[]>([])
  const [selectedProjects, setSelectedProjects] = useState<Project[]>([])
  const [isSendToAll, setIsSendToAll] = useState(false)
  const currentUser = useSelector(getCurrentUser)
  const [sendMissingHours] = useSendMissingHoursNotificationMutation()
  const [tabValue, setTabValue] = React.useState(0)

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
    setSelectedEmployees([])
    setSelectedLeaders([])
    setSelectedProjects([])
  }

  const handleConfirm = async () => {
    setIsConfirmationDialogOpen(false)
    setOpen(false)

    try {
      await sendMissingHours({
        employeeIds: tabValue === 0 && !isSendToAll ? selectedEmployees.map(u => u.id) : [],
        leaderIds: tabValue === 1 && !isSendToAll ? selectedLeaders.map(l => l.id) : [],
        projectIds: tabValue === 2 && !isSendToAll ? selectedProjects.map(l => l.id) : [],
        sendToAllEmployees: isSendToAll,
      }).unwrap()

      setSelectedEmployees([])
      setSelectedLeaders([])
      setSelectedProjects([])

      displayNotification({
        open: false,
        type: 'success',
        message: t('components.notificationSidebar.missingHoursNotification.successMessage'),
        timeout: 5000,
      })
    } catch (error) {
      displayNotification({
        open: false,
        type: 'error',
        message: t('components.notificationSidebar.missingHoursNotification.errorMessage'),
        timeout: 5000,
      })
    }
  }

  const markAllEmployees = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsSendToAll(event.target.checked)
  }

  const toggleConfirmDialog = () => {
    setIsConfirmationDialogOpen(true)
  }

  const checkUnsavedChanges = useCallback(() => {
    return selectedEmployees.length > 0 || selectedLeaders.length > 0 || selectedProjects.length > 0 || isSendToAll
  }, [selectedEmployees, selectedLeaders, selectedProjects, isSendToAll])

  const resetDialogState = () => {
    setSelectedEmployees([])
    setSelectedLeaders([])
    setSelectedProjects([])
    setIsSendToAll(false)
  }

  const handleCancel = () => {
    if (checkUnsavedChanges()) {
      setIsCancelDialogOpen(true)
    } else {
      resetDialogState()
      setOpen(false)
    }
  }

  useEffect(() => {
    setIsSendButtonDisabled(!checkUnsavedChanges())
  }, [checkUnsavedChanges])

  useEffect(() => {
    if (isSendToAll) {
      setTabValue(0)
      setSelectedEmployees([])
      setSelectedLeaders([])
      setSelectedProjects([])
    }
  }, [isSendToAll])

  return (
    <React.Fragment>
      {!button ? (
        <SettingTile
          icon={<NotificationsActiveIcon />}
          title={t('page.adminsPage.generalSettings.missingHoursNotification.title')}
          description={t('page.adminsPage.generalSettings.missingHoursNotification.description')}
          onSelect={() => setOpen(prev => !prev)}
        />
      ) : (
        <Button
          onClick={() => setOpen(prev => !prev)}
          size="small"
          color="primary"
          variant="contained"
          sx={{ width: 'fit-content' }}
        >
          <CheckIcon sx={{ fontSize: '15px', right: '1px', position: 'relative' }} />
          {t('page.adminsPage.generalSettings.employeeMonthlyHours.notify')}
        </Button>
      )}

      <Dialog
        sx={{ color: 'text.base' }}
        PaperProps={{ elevation: 2, sx: { maxWidth: '600px', width: '100%' } }}
        open={open}
        onClose={(_, reason) => {
          if (reason === 'backdropClick' && checkUnsavedChanges()) {
            setIsCancelDialogOpen(true)
          } else {
            setOpen(false)
            resetDialogState()
          }
        }}
        aria-labelledby={'send-administrative-modification-dialog-title'}
      >
        <StyledContainerColumnLeft
          sx={{
            p: 4,
            gap: getGap(isSmallScreen),
          }}
        >
          <StyledContainerRowStart sx={{ width: '100%' }}>
            <StyledContainerColumnLeft sx={{ width: '100%' }}>
              <Typography
                component={'h3'}
                variant="h4"
                color="text.highEmphasis"
                id={'send-administrative-modification-dialog-title'}
              >
                {t('components.notificationSidebar.missingHoursNotification.title')}
              </Typography>
              <Typography fontSize={'small'} fontStyle={'italic'} color="text.base">
                {t('components.notificationSidebar.missingHoursNotification.description')}
              </Typography>
            </StyledContainerColumnLeft>
          </StyledContainerRowStart>

          <FormControlLabel
            control={<Checkbox style={{ marginRight: 8 }} checked={isSendToAll} onChange={markAllEmployees} />}
            label={t('components.notificationSidebar.missingHoursNotification.sendAll')}
            sx={{
              marginTop: -2,
              marginBottom: -2,
              color: isSendToAll ? 'primary.main' : '',
              '& .MuiFormControlLabel-label': {
                fontWeight: 500,
              },
              width: 'fit-content',
            }}
          />

          {!isSendToAll && (
            <Stack direction={'column'} sx={{ gap: 2 }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                indicatorColor="secondary"
                textColor="inherit"
                variant="fullWidth"
                aria-label="full width tabs example"
              >
                <Tab label={t('components.notificationSidebar.missingHoursNotification.item1')} />
                <Tab label={t('components.notificationSidebar.missingHoursNotification.item2')} />
                <Tab label={t('components.notificationSidebar.missingHoursNotification.item3')} />
              </Tabs>
              {tabValue === 0 && (
                <Stack direction={'column'} sx={{ gap: 2 }}>
                  <Typography fontSize={'small'} fontStyle={'italic'} color="text.base">
                    {t('components.notificationSidebar.missingHoursNotification.descriptionItem1')}
                  </Typography>
                  <AutocompleteEmployees
                    selectedEmployees={selectedEmployees}
                    setSelectedEmployees={setSelectedEmployees}
                    currentUser={currentUser}
                  />
                </Stack>
              )}
              {tabValue === 1 && (
                <Stack direction={'column'} sx={{ gap: 2 }}>
                  <Typography fontSize={'small'} fontStyle={'italic'} color="text.base">
                    {t('components.notificationSidebar.missingHoursNotification.descriptionItem2')}
                  </Typography>
                  <AutocompleteLeaders
                    selectedLeaders={selectedLeaders}
                    setSelectedLeaders={setSelectedLeaders}
                    currentUser={currentUser}
                  />
                </Stack>
              )}
              {tabValue === 2 && (
                <Stack direction={'column'} sx={{ gap: 2 }}>
                  <Typography fontSize={'small'} fontStyle={'italic'} color="text.base">
                    {t('components.notificationSidebar.missingHoursNotification.descriptionItem3')}
                  </Typography>
                  <AutocompleteProjects selectedProjects={selectedProjects} setSelectedProjects={setSelectedProjects} />
                </Stack>
              )}
            </Stack>
          )}

          <Stack gap={2} justifyContent="flex-end" direction="row">
            <Button color="secondary" onClick={handleCancel} sx={{ fontWeight: 'bold' }}>
              {t('components.notificationSidebar.administrativeNotification.cancel')}
            </Button>
            <Button
              color="primary"
              variant="contained"
              size="medium"
              disabled={isSendButtonDisabled}
              onClick={toggleConfirmDialog}
            >
              {t('components.notificationSidebar.administrativeNotification.send')}
            </Button>
          </Stack>
        </StyledContainerColumnLeft>
      </Dialog>
      <ConfirmationDialog
        open={isConfirmationDialogOpen}
        setOpen={setIsConfirmationDialogOpen}
        handleConfirm={handleConfirm}
        title={t('components.notificationSidebar.dialog.sendNotificationTitle')}
        content={t('components.notificationSidebar.dialog.sendNotificationContent')}
      />
      <DiscardChangesDialog
        open={isCancelDialogOpen}
        checkUnsavedChanges={checkUnsavedChanges}
        setIsCancelDialogOpen={setIsCancelDialogOpen}
        actionOnConfirm={() => {
          setOpen(false)
          resetDialogState()
        }}
      />
    </React.Fragment>
  )
}

export default MissingHoursNotificationDialog
